import { useState, useEffect } from 'react';
import { type Event } from '@/types/event';
import { EventCard } from '../EventCard/EventCard';
import { useScrollFocus } from '@/hooks/useScrollFocus';
import { useHoverFocus } from '@/hooks/useHoverFocus';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import styles from './EventList.module.css';

interface EventListProps {
  events: Event[];
}

/**
 * Main events container with focus logic
 * Combines scroll-based focus (mobile) with hover-based focus (desktop)
 */
export function EventList({ events }: EventListProps) {
  const isDesktop = useIsDesktop();
  const { focusedIndex, scrollContainerRef, scrollToIndex, isReady } = useScrollFocus(events.length);
  const { hoveredIndex, handleMouseEnter, handleMouseLeave } = useHoverFocus();

  const [expandedIndex, setExpandedIndex] = useState<number | null>(null);

  // Handle deep linking (e.g. #/event/123)
  useEffect(() => {
    if (!isReady || events.length === 0) return;

    const hash = window.location.hash;
    const match = hash.match(/#\/event\/(\d+)/);
    
    if (match) {
      const eventId = match[1];
      const index = events.findIndex(e => e.id === eventId);
      
      if (index !== -1) {
        scrollToIndex(index);
        setExpandedIndex(index);
        // Clear hash after handling to prevent re-scroll issues or weird history
        history.replaceState(null, '', ' ');
      }
    }
  }, [isReady, events, scrollToIndex]);

  const handleCardClick = (index: number) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  return (
    <div ref={scrollContainerRef} className={styles.eventList}>
      {events.map((event, index) => {
        const isFocused = isDesktop
          ? hoveredIndex !== null
            ? index === hoveredIndex
            : index === focusedIndex
          : index === focusedIndex;

        const isExpanded = index === expandedIndex;

        return (
          <EventCard
            key={event.id}
            event={event}
            isFocused={isFocused}
            isExpanded={isExpanded}
            onMouseEnter={() => handleMouseEnter(index)}
            onMouseLeave={handleMouseLeave}
            onClick={() => handleCardClick(index)}
          />
        );
      })}
    </div>
  );
}
